import { useEffect, useState, type JSX } from 'react'
import { useSearchParams } from 'react-router'
import {
  Contact, Download, Loader2, Plus, RefreshCw, Search, Users, X,
} from 'lucide-react'
import { useClients } from '../../../hooks/useClients.js'
import { useClientGroups } from '../../../hooks/useClientGroups.js'
import ClientsTable from '../../../components/agent/clients/ClientsTable.js'
import ClientsPaginationBar from '../../../components/agent/clients/ClientsPaginationBar.js'
import CreateClientPanel, { type CreateClientPanelMode } from '../../../components/agent/clients/CreateClientPanel.js'
import ClientViewDrawer, { type ClientViewDrawerTab } from '../../../components/agent/clients/ClientViewDrawer.js'
import ClientsFilterToolbar, {
  deriveStatusFilter, type ClientStatusFilter,
} from '../../../components/agent/clients/ClientsFilterToolbar.js'

/* ── Design tokens: radius capped at 3px across the whole surface ──
   Mirrors ConversationInbox so the Clients tab reads as the same product. */
const R = 'rounded-[3px]'
const ICON_BTN = `flex h-9 w-9 shrink-0 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900`

/* ── Loading / empty states — match the inbox's shimmer + empty card ── */

function ShimmerBar({ className, style }: { className?: string; style?: React.CSSProperties }): JSX.Element {
  return (
    <div
      className={`${className ?? ''} relative overflow-hidden bg-slate-100 ${R}`}
      style={style}
      aria-hidden="true"
    >
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
  )
}

function EmptyState({ searchQuery, statusFilter, loading, onCreateNew }: {
  searchQuery: string
  statusFilter: ClientStatusFilter
  loading: boolean
  onCreateNew?: () => void
}): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-20">
        <Loader2 className="mb-3 h-6 w-6 animate-spin text-slate-300" />
        <p className="text-[13px] text-slate-400">Loading clients…</p>
      </div>
    )
  }
  const isDefaultView = !searchQuery && statusFilter === 'all'
  const message = searchQuery
    ? `No clients match "${searchQuery}". Try a different term or clear your filters.`
    : statusFilter !== 'all'
      ? 'Nothing matches this filter right now.'
      : 'No clients yet. Create your first client to get started.'
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-20">
      <div className={`mb-5 flex h-14 w-14 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
        <Contact className="h-6 w-6" />
      </div>
      <h3 className="mb-1.5 text-[15px] font-bold text-[#0f172a]">No clients</h3>
      <p className="mb-6 max-w-sm text-center text-[13px] leading-relaxed text-slate-400">{message}</p>
      {isDefaultView && onCreateNew && (
        <button onClick={onCreateNew} className={`flex h-9 items-center gap-2 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}>
          <Plus className="h-4 w-4" />New client
        </button>
      )}
    </div>
  )
}

export default function ClientsListPage(): JSX.Element {
  const clientsState = useClients()
  const {
    clients, total, loading, search, setSearch, status, locked,
    page, pageSize, setPage, setPageSize, refetch,
  } = clientsState
  const { groups } = useClientGroups()

  const [panel, setPanel] = useState<{ open: boolean; mode: CreateClientPanelMode; clientId?: string }>({
    open: false, mode: 'create',
  })
  const [viewDrawer, setViewDrawer] = useState<{
    open: boolean; clientId: string | null; tab: ClientViewDrawerTab
  }>({ open: false, clientId: null, tab: 'basicInfo' })

  // Open the client drawer when another view links here with ?clientId=...
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const clientId = searchParams.get('clientId')
    if (clientId) {
      setViewDrawer({ open: true, clientId, tab: 'basicInfo' })
    }
  }, [searchParams])

  // Search mirrors the inbox: typed locally, committed on Enter / icon click.
  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => { setSearchInput(search) }, [search])

  const statusFilter = deriveStatusFilter(status, locked)

  const handleSearch = (): void => { setSearch(searchInput) }
  const handleClearSearch = (): void => { setSearchInput(''); setSearch('') }

  const openCreate = (): void => setPanel({ open: true, mode: 'create' })
  const openView = (id: string): void => setViewDrawer({ open: true, clientId: id, tab: 'basicInfo' })
  const openHistory = (id: string): void => setViewDrawer({ open: true, clientId: id, tab: 'contactHistory' })
  const openEdit = (id: string): void => setPanel({ open: true, mode: 'edit', clientId: id })
  const closePanel = (): void => setPanel((p) => ({ ...p, open: false }))
  const closeViewDrawer = (): void => setViewDrawer({ open: false, clientId: null, tab: 'basicInfo' })

  const groupName = (id: string): string | undefined => groups.find((g) => g.id === id)?.name

  /** Downloads the current page of filtered rows as CSV. */
  const exportCsv = (): void => {
    const esc = (v: string): string => `"${v.replace(/"/g, '""')}"`
    const rows = clients.map((c) =>
      [c.full_name ?? '', c.email, c.phone_number ?? '', groupName(c.group_id) ?? '', c.status ? 'Active' : 'Inactive', c.locked ? 'Yes' : '', c.createdAt.slice(0, 10)]
        .map(esc)
        .join(','))
    const csv = ['Name,Email,Phone,Group,Status,Locked,Created', ...rows].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `clients-page-${page}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="client-screen flex h-full flex-col bg-[#F4F6F8]">
      {/* ── Header: identity · search · actions ── */}
      <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-6 py-3.5">
        <div className="flex shrink-0 items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center border border-blue-200 ${R} bg-blue-50 text-[#3366FF]`}>
            <Users className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900">Clients</h1>
              <span className={`${R} border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600`}>Total: <span className="font-mono">{total.toLocaleString()}</span></span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              {total.toLocaleString()} client{total !== 1 ? 's' : ''} across {groups.length} group{groups.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2.5">
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch() } }}
              className={`w-full py-1.5 pl-9 pr-8 text-xs ${R} border border-slate-300 bg-slate-50 text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#3366FF] focus:bg-white focus:ring-1 focus:ring-[#3366FF]`}
            />
            {searchInput && (
              <button onClick={handleClearSearch} className={`absolute right-1.5 top-1/2 -translate-y-1/2 ${R} p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`} title="Clear">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <button onClick={() => void refetch()} className={ICON_BTN} title="Refresh client list">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportCsv} className={ICON_BTN} title="Export this page as CSV" disabled={clients.length === 0}>
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={openCreate}
            className={`flex shrink-0 items-center gap-1.5 ${R} bg-[#3366FF] px-3.5 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-[#2952CC]`}
          >
            <Plus className="h-4 w-4" />New Client
          </button>
        </div>
      </header>

      {/* ── Toolbar: filter · group — left; sort · date · reset — right ── */}
      <ClientsFilterToolbar clients={clientsState} groups={groups} />

      {/* ── Top loading bar — visible on refetch / page change ── */}
      {loading && clients.length > 0 && (
        <div className="relative h-0.5 w-full overflow-hidden bg-transparent">
          <div className="absolute inset-0 animate-[shimmer_1s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-[#3366FF] to-transparent" />
        </div>
      )}

      {/* ── Data table ── */}
      <div className="scrollbar-thin relative flex-1 overflow-auto bg-white">
        {loading && clients.length === 0 ? (
          <div aria-hidden="true">
            <div className="flex h-11 items-center gap-4 border-b border-slate-200 bg-slate-50 px-4">
              {[16, 120, 80, 80, 80, 80, 130].map((w, i) => <ShimmerBar key={i} className="h-2.5" style={{ width: w }} />)}
            </div>
            {Array.from({ length: 10 }).map((_, r) => (
              <div key={r} className="flex h-[52px] items-center gap-4 border-b border-slate-200 px-4">
                {[16, 140, 70, 94, 80, 80, 130].map((w, c) => <ShimmerBar key={c} className="h-3" style={{ width: w }} />)}
              </div>
            ))}
          </div>
        ) : clients.length === 0 && !loading ? (
          <EmptyState searchQuery={search} statusFilter={statusFilter} loading={loading} onCreateNew={openCreate} />
        ) : (
          <>
            {loading && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <Loader2 className="h-6 w-6 animate-spin text-[#3366FF]" />
              </div>
            )}
            <ClientsTable
              clients={clients}
              groups={groups}
              loading={loading}
              onView={openView}
              onHistory={openHistory}
              onEdit={openEdit}
            />
          </>
        )}
      </div>

      {/* ── Footer ── */}
      {total > 0 && (
        <ClientsPaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <CreateClientPanel
        open={panel.open}
        mode={panel.mode}
        {...(panel.clientId ? { clientId: panel.clientId } : {})}
        onClose={closePanel}
        onCreated={() => { closePanel(); void refetch() }}
      />

      {viewDrawer.clientId && (
        <ClientViewDrawer
          open={viewDrawer.open}
          clientId={viewDrawer.clientId}
          initialTab={viewDrawer.tab}
          onClose={closeViewDrawer}
        />
      )}
    </div>
  )
}
